import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import logger from '../Helpers/logger';
import { clearState } from './clearSlice';

const initialState = {
    channelConversations: {},
    channelUiState: {}, // channelId -> { hasMoreForward, hasMoreBackward, loadingForward, loadingBackward }
    channelSearch: {}, // channelId -> { searchText, searchResults, currentSearchIndex, isSearching }
};

const getRelativeDateLabel = timestampOrDate => {
    const messageDate = new Date(timestampOrDate);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    // Normalize all to midnight for accurate day-only comparison
    const normalize = d => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const msg = normalize(messageDate);
    const todayOnly = normalize(today);
    const yesterdayOnly = normalize(yesterday);

    if (msg.getTime() === todayOnly.getTime()) {
        return 'Today';
    } else if (msg.getTime() === yesterdayOnly.getTime()) {
        return 'Yesterday';
    } else {
        // Use Intl.DateTimeFormat for localization instead of date-fns (no dependency)
        return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: '2-digit',
            year: 'numeric',
        }).format(messageDate);
    }
};

const channelConversationSlice = createSlice({
    name: 'channelConversationData',
    initialState,
    reducers: {
        // Set loading true/false
        setChannelConversationLoading: (state, action) => {
            const { channelId, loading } = action.payload;
            state.channelConversations[channelId] = {
                ...(state.channelConversations[channelId] || {}),
                loading,
            };
        },

        appendChannelMessages: (state, action) => {
            const { channelId, messages } = action.payload;

            if (!state.channelConversations[channelId]) {
                state.channelConversations[channelId] = {
                    messages: [],
                    lastSequenceId: 0,
                    hasMore: true,
                    hasMoreBackward: true,
                    loading: false,
                    loadingBackward: false,
                };
            }

            let prevMessages = state.channelConversations[channelId].messages || [];

            // ✅ 1. Find all dateKeys in incoming messages
            const incomingDateKeys = new Set(messages.filter(m => m.type === 'date-label').map(m => m.dateKey));

            // ✅ 2. Remove any matching date-labels from Redux (bottom overlap)
            prevMessages = prevMessages.filter(m => !(m.type === 'date-label' && incomingDateKeys.has(m.dateKey)));

            // ✅ 3. Deduplicate and merge
            const combined = [...prevMessages, ...messages];
            const seen = new Set();
            const deduped = [];

            for (const msg of combined) {
                const key = msg.type === 'date-label' ? `label-${msg.dateKey}` : msg.messageId;
                if (!seen.has(key)) {
                    seen.add(key);
                    deduped.push(msg);
                }
            }

            state.channelConversations[channelId].messages = deduped;
        },

        addChannelMessage: (state, action) => {
            const { channelId, message } = action.payload;
            if (!message) return;

            // Deep clone channel safely
            const existingChannel = state.channelConversations[channelId] || {
                messages: [],
                lastSequenceId: 0,
                hasMore: true,
                hasMoreBackward: true,
                loading: false,
                loadingBackward: false,
            };

            const channel = {
                ...existingChannel,
                messages: [...(existingChannel.messages || [])],
            };

            const updatedMessages = channel.messages;
            const messageId = message.messageId;
            const msgDate = new Date(message.messageTime).toISOString().split('T')[0];
            const normalizedMessage = { ...message, type: message.type || 'message' };

            const existingIndex = updatedMessages.findIndex(m => m.messageId === messageId);
            if (existingIndex !== -1) {
                const existingMsg = updatedMessages[existingIndex];
                const mergedContent = {
                    ...existingMsg.content,
                    ...normalizedMessage.content,
                    media: {
                        ...(existingMsg.content?.media || {}),
                        ...(normalizedMessage.content?.media || {}),
                    },
                };
                updatedMessages[existingIndex] = {
                    ...existingMsg,
                    ...normalizedMessage,
                    content: mergedContent,
                };
            } else {
                const hasDateLabel = updatedMessages.some(m => m.type === 'date-label' && m.dateKey === msgDate);

                if (!hasDateLabel) {
                    const dateLabel = {
                        type: 'date-label',
                        label: getRelativeDateLabel(msgDate),
                        dateKey: msgDate,
                        messageId: `date-${msgDate}`,
                    };
                    updatedMessages.unshift(dateLabel);
                }

                updatedMessages.unshift(normalizedMessage);
            }

            // ✅ Assign back isolated channel
            state.channelConversations[channelId] = channel;
        },

        /**
         * Batch add missing messages to multiple channels in a single update
         * Optimized for handling MISSING_MESSAGE_PUBLISHED events
         * Replaces placeholder missing messages based on sequenceId
         */
        batchAddMissingMessages: (state, action) => {
            const { messagesByChannel } = action.payload;

            // Process all channels in a single Redux update
            Object.entries(messagesByChannel).forEach(([channelId, channelData]) => {
                const { messages } = channelData;

                // Skip if channel doesn't exist
                if (!state.channelConversations[channelId]) {
                    logger.warn(`Channel ${channelId} not found in state, skipping missing messages`);
                    return;
                }

                const existingMessages = state.channelConversations[channelId].messages || [];

                // Strip existing date labels and separators, keep only actual messages
                const existingMessagesOnly = existingMessages.filter(msg => msg.type === 'message');

                // Add type field to incoming messages
                const incomingMessagesWithType = messages.map(msg => ({
                    ...msg,
                    type: 'message',
                }));

                // Replace placeholder missing messages based on sequenceId
                const updatedMessages = [...existingMessagesOnly];

                incomingMessagesWithType.forEach(newMsg => {
                    // First, try to find by sequenceId (to replace missing_message placeholders)
                    const sequenceIndex = updatedMessages.findIndex(
                        msg => msg.sequenceId === newMsg.sequenceId && msg.messageType === 'missing_message',
                    );

                    if (sequenceIndex !== -1) {
                        // Replace placeholder missing message with actual message
                        logger.debug(
                            `🔄 Replacing placeholder message (sequenceId: ${newMsg.sequenceId}) with actual message`,
                        );
                        updatedMessages[sequenceIndex] = newMsg;
                    } else {
                        // Check if message already exists by messageId
                        const existingIndex = updatedMessages.findIndex(msg => msg.messageId === newMsg.messageId);

                        if (existingIndex !== -1) {
                            // Update existing message
                            const existingMsg = updatedMessages[existingIndex];
                            const mergedContent = {
                                ...existingMsg.content,
                                ...newMsg.content,
                                media: {
                                    ...(existingMsg.content?.media || {}),
                                    ...(newMsg.content?.media || {}),
                                },
                            };

                            updatedMessages[existingIndex] = {
                                ...existingMsg,
                                ...newMsg,
                                content: mergedContent,
                            };
                        } else {
                            // Add new message at the beginning (0th position)
                            updatedMessages.unshift(newMsg);
                        }
                    }
                });

                const finalMessages = updatedMessages;

                state.channelConversations[channelId].messages = finalMessages;
            });
        },

        updateMessageReaction: (state, action) => {
            const { channelId, messageId, reactions } = action.payload;
            const messages = state.channelConversations[channelId]?.messages || [];

            const msgIndex = messages.findIndex(msg => msg.messageId === messageId);
            if (msgIndex === -1) return;

            const updatedMessage = {
                ...messages[msgIndex],
                reactions: { ...reactions },
            };

            const updatedMessages = [...messages];
            updatedMessages[msgIndex] = updatedMessage;

            state.channelConversations[channelId] = {
                ...(state.channelConversations[channelId] || {}),
                messages: updatedMessages,
            };
        },

        updateMediaStatus(state, action) {
            const { channelId, messageId, is_downloaded, is_uploading, local_path } = action.payload;
            if (state.channelConversations[channelId]) {
                state.channelConversations[channelId].messages = state.channelConversations?.[channelId]?.messages?.map(
                    message => {
                        if (message.messageId === messageId) {
                            return {
                                ...message,
                                content: {
                                    ...message.content,
                                    media: {
                                        ...message.content.media,
                                        ...(is_downloaded !== undefined && { is_downloaded }),
                                        ...(is_uploading !== undefined && { is_uploading }),
                                        ...(local_path !== undefined && { local_path }),
                                    },
                                },
                            };
                        }
                        return message;
                    },
                );
            }
        },

        setChannelConversationLastSequenceId: (state, action) => {
            const { channelId, lastSequenceId } = action.payload;
            state.channelConversations[channelId] = {
                ...(state.channelConversations[channelId] || {}),
                lastSequenceId,
            };
        },

        setChannelConversationHasMore: (state, action) => {
            const { channelId, hasMore } = action.payload;
            state.channelConversations[channelId] = {
                ...(state.channelConversations[channelId] || {}),
                hasMore,
            };
        },

        setChannelConversationHasMoreBackward: (state, action) => {
            const { channelId, hasMoreBackward } = action.payload;
            state.channelConversations[channelId] = {
                ...(state.channelConversations[channelId] || {}),
                hasMoreBackward,
            };
        },

        setChannelConversationLoadingBackward: (state, action) => {
            const { channelId, loadingBackward } = action.payload;
            state.channelConversations[channelId] = {
                ...(state.channelConversations[channelId] || {}),
                loadingBackward,
            };
        },

        prependChannelMessages: (state, action) => {
            const { channelId, messages } = action.payload;

            if (!state.channelConversations[channelId]) {
                state.channelConversations[channelId] = {
                    messages: [],
                    lastSequenceId: 0,
                    hasMore: true,
                    hasMoreBackward: true,
                    loading: false,
                    loadingBackward: false,
                };
            }

            const prevMessages = state.channelConversations[channelId].messages || [];

            // ✅ 1. Find all dateKeys already in Redux
            const existingDateKeys = new Set(prevMessages.filter(m => m.type === 'date-label').map(m => m.dateKey));

            // ✅ 2. Filter out any date-labels in incoming payload that already exist in Redux
            const cleanedIncoming = messages.filter(m => !(m.type === 'date-label' && existingDateKeys.has(m.dateKey)));

            // ✅ 3. Merge + dedupe
            const combined = [...cleanedIncoming, ...prevMessages];
            const seen = new Set();
            const deduped = [];

            for (const msg of combined) {
                const key = msg.type === 'date-label' ? `label-${msg.dateKey}` : msg.messageId;
                if (!seen.has(key)) {
                    seen.add(key);
                    deduped.push(msg);
                }
            }

            state.channelConversations[channelId].messages = deduped;
        },

        recalculateChannelMessages: (state, action) => {
            const { channelId, lastReadMessageId, unreadCount } = action.payload;

            const channel = state.channelConversations[channelId];
            if (!channel?.messages) return;

            // Remove any existing unread separators, but keep date labels
            const messagesOnly = channel.messages.filter(msg => msg.type === 'message' || msg.type === 'date-label');

            let finalMessages = [...messagesOnly];

            // If there are unread messages and lastReadMessageId exists, add separator
            if (unreadCount > 0 && lastReadMessageId) {
                // Find the index of the lastReadMessageId in the messages with date labels
                const lastReadIndex = finalMessages.findIndex(msg => msg.messageId === lastReadMessageId);

                if (lastReadIndex !== -1) {
                    const unreadSeparator = {
                        type: 'unread-separator',
                        label: `${unreadCount} Unread Message${unreadCount > 1 ? 's' : ''}`,
                        messageId: lastReadMessageId + '-unread-separator',
                    };

                    // Insert the separator right after the lastReadMessageId
                    finalMessages.splice(lastReadIndex, 0, unreadSeparator);
                }
            }
            // If unreadCount is 0, finalMessages will only contain messages and date labels (no separators)

            // Update the channel state
            state.channelConversations[channelId].messages = finalMessages;
        },

        toggleMessageSelection: (state, action) => {
            const { channelId, messageId, selectionType } = action.payload;

            const channel = state.channelConversations[channelId];
            if (!channel?.messages) return;

            // Optionally update selection type
            if (selectionType) {
                channel.selectionType = selectionType;
            }

            // Toggle isSelected on the target message
            const msg = channel.messages.find(m => m.messageId === messageId);
            if (msg) {
                msg.isSelected = !msg.isSelected;
            }
        },

        resetSelectedMessages: (state, action) => {
            const { channelId } = action.payload;
            const messages = state.channelConversations[channelId]?.messages;
            if (!messages) return;

            state.channelConversations[channelId].selectionType = '';
            state.channelConversations[channelId].messages = messages.map(msg =>
                msg.isSelected ? { ...msg, isSelected: false } : msg,
            );
        },

        updateDeleteMessages: (state, action) => {
            const { channelId, messageIds = [], isDeleteForAll = false } = action.payload;
            const messages = state.channelConversations[channelId]?.messages;

            if (!messages?.length || !messageIds.length) return;

            if (isDeleteForAll) {
                // For delete for everyone: Flag messages as deleted
                messages.forEach(msg => {
                    if (messageIds.includes(msg.messageId)) {
                        msg.isDeletedForEveryone = true;
                    }
                });
            } else {
                // Get date key from timestamp (prefer messageTime, fallback to createdAt)
                const getDateKey = msg => {
                    const ts = msg.messageTime || msg.createdAt || Date.now();
                    return moment(ts).format('YYYY-MM-DD');
                };

                // For delete for me: Track dateKeys before removing messages
                const deletedMessageDateKeys = new Set();
                messages.forEach(msg => {
                    if (messageIds.includes(msg.messageId) && msg.type === 'message') {
                        const dateKey = getDateKey(msg);
                        deletedMessageDateKeys.add(dateKey);
                    }
                });

                // Remove messages from the array for delete for me
                state.channelConversations[channelId].messages = messages.filter(
                    msg => !messageIds.includes(msg.messageId),
                );

                // Remove date-labels if the deleted message was the last message of that day
                const remainingMessages = state.channelConversations[channelId].messages;
                deletedMessageDateKeys.forEach(dateKey => {
                    // Check if there are any remaining messages for this dateKey
                    const hasRemainingMessages = remainingMessages.some(
                        msg => msg.type === 'message' && getDateKey(msg) === dateKey,
                    );

                    // If no remaining messages exist for this date, remove the date-label
                    if (!hasRemainingMessages) {
                        const dateLabelIndex = remainingMessages.findIndex(
                            msg => msg.type === 'date-label' && msg.dateKey === dateKey,
                        );
                        if (dateLabelIndex !== -1) {
                            remainingMessages.splice(dateLabelIndex, 1);
                        }
                    }
                });
            }
        },
        // UI State reducers for pagination/loading
        setChannelHasMore: (state, action) => {
            const { channelId, direction, value } = action.payload;
            if (!state.channelUiState[channelId]) {
                state.channelUiState[channelId] = {
                    hasMoreForward: false,
                    hasMoreBackward: false,
                    loadingForward: false,
                    loadingBackward: false,
                    initialized: true,
                };
            }
            if (direction === 'forward') {
                const next = Boolean(value);
                state.channelUiState[channelId].hasMoreForward = next;
            }
            if (direction === 'backward') {
                const next = Boolean(value);
                state.channelUiState[channelId].hasMoreBackward = next;
            }
        },

        setChannelLoading: (state, action) => {
            const { channelId, direction, value } = action.payload; // direction: 'forward' | 'backward'
            if (!state.channelUiState[channelId]) {
                state.channelUiState[channelId] = {
                    hasMoreForward: false,
                    hasMoreBackward: false,
                    loadingForward: false,
                    loadingBackward: false,
                    initialized: true,
                };
            }
            if (direction === 'initialLoad') {
                const next = Boolean(value);
                state.channelUiState[channelId].loadingInitialLoad = next;
            }
            if (direction === 'forward') {
                const next = Boolean(value);
                state.channelUiState[channelId].loadingForward = next;
            }
            if (direction === 'backward') {
                const next = Boolean(value);
                state.channelUiState[channelId].loadingBackward = next;
            }
        },

        resetChannelUiState: (state, action) => {
            const { channelId } = action.payload;
            if (state.channelUiState[channelId]) {
                delete state.channelUiState[channelId];
            }
        },

        // Reset UI state only when switching to a different channel
        resetChannelUiStateOnSwitch: (state, action) => {
            const { channelId } = action.payload;
            if (state.channelUiState[channelId]) {
                delete state.channelUiState[channelId];
            }
        },

        setChannelSearchText: (state, action) => {
            const { channelId, searchText } = action.payload;
            if (!state.channelSearch[channelId]) {
                state.channelSearch[channelId] = {
                    searchText: '',
                    searchResults: [],
                    currentSearchIndex: -1,
                    isSearching: false,
                };
            }
            state.channelSearch[channelId].searchText = searchText;
        },
        setChannelSearchResults: (state, action) => {
            const { channelId, searchResults } = action.payload;
            if (!state.channelSearch[channelId]) {
                state.channelSearch[channelId] = {
                    searchText: '',
                    searchResults: [],
                    currentSearchIndex: -1,
                    isSearching: false,
                };
            }
            state.channelSearch[channelId].searchResults = searchResults;
        },
        setChannelSearchIndex: (state, action) => {
            const { channelId, index } = action.payload;
            if (!state.channelSearch[channelId]) return;
            state.channelSearch[channelId].currentSearchIndex = index;
        },
        setChannelIsSearching: (state, action) => {
            const { channelId, isSearching } = action.payload;
            if (!state.channelSearch[channelId]) {
                state.channelSearch[channelId] = {
                    searchText: '',
                    searchResults: [],
                    currentSearchIndex: -1,
                    isSearching: false,
                };
            }
            state.channelSearch[channelId].isSearching = isSearching;
        },
        removeUnreadSeparatorFromMessages: (state, action) => {
            const { channelId } = action.payload;
            if (state.channelConversations[channelId]) {
                state.channelConversations[channelId].messages = state.channelConversations[channelId].messages.filter(
                    msg => msg.type !== 'unread-separator',
                );
            }
        },

        updateUnreadSeparatorLabel: (state, action) => {
            const { channelId, incrementBy = 1 } = action.payload;
            const channel = state.channelConversations[channelId];
            if (!channel?.messages) return;

            // Find and update the unread separator label by incrementing
            const unreadSeparator = channel.messages.find(msg => msg.type === 'unread-separator');
            if (unreadSeparator) {
                // Extract existing count from the label if it exists
                const existingLabel = unreadSeparator.label || '';
                const existingCountMatch = existingLabel.match(/^(\d+)\s+Unread/);
                const existingCount = existingCountMatch ? parseInt(existingCountMatch[1], 10) : 0;

                // Increment the count
                const newCount = existingCount + incrementBy;
                unreadSeparator.label = `${newCount} Unread Message${newCount > 1 ? 's' : ''}`;
            }
        },

        decrementUnreadSeparatorLabel: (state, action) => {
            const { channelId, decrementBy = 1 } = action.payload;
            const channel = state.channelConversations[channelId];
            if (!channel?.messages) return;

            // Find and update the unread separator label by decrementing
            const unreadSeparator = channel.messages.find(msg => msg.type === 'unread-separator');
            if (unreadSeparator) {
                // Extract existing count from the label
                const existingLabel = unreadSeparator.label || '';
                const existingCountMatch = existingLabel.match(/^(\d+)\s+Unread/);
                const existingCount = existingCountMatch ? parseInt(existingCountMatch[1], 10) : 0;

                // Decrement the count
                const newCount = Math.max(0, existingCount - decrementBy);

                if (newCount > 0) {
                    unreadSeparator.label = `${newCount} Unread Message${newCount > 1 ? 's' : ''}`;
                } else {
                    // Remove the unread separator if count reaches 0
                    channel.messages = channel.messages.filter(msg => msg.type !== 'unread-separator');
                }
            }
        },

        clearChannelSearch: (state, action) => {
            const { channelId } = action.payload;
            if (state.channelSearch[channelId]) {
                state.channelSearch[channelId] = {
                    searchText: '',
                    searchResults: [],
                    currentSearchIndex: -1,
                    isSearching: true,
                };
            }
        },

        // Clear conversation data for all channels except the active one
        clearChannelsConversationExceptActive: (state, action) => {
            const { activeChannelId } = action.payload;
            const allChannelIds = Object.keys(state.channelConversations);

            allChannelIds.forEach(channelId => {
                if (channelId !== activeChannelId) {
                    // Clear conversation data
                    delete state.channelConversations[channelId];
                    // Clear UI state
                    if (state.channelUiState[channelId]) {
                        delete state.channelUiState[channelId];
                    }
                    // Clear search state
                    if (state.channelSearch[channelId]) {
                        delete state.channelSearch[channelId];
                    }
                }
            });
        },

        // Clear conversation data for a specific channel
        clearChannelConversation: (state, action) => {
            const { channelId } = action.payload;
            if (channelId) {
                // Clear conversation data
                if (state.channelConversations[channelId]) {
                    delete state.channelConversations[channelId];
                }
                // Clear UI state
                if (state.channelUiState[channelId]) {
                    delete state.channelUiState[channelId];
                }
                // Clear search state
                if (state.channelSearch[channelId]) {
                    delete state.channelSearch[channelId];
                }
            }
        },
    },

    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
    },
});

export const {
    setChannelConversationLoading,
    appendChannelMessages,
    prependChannelMessages,
    recalculateChannelMessages,
    setChannelConversationLastSequenceId,
    setChannelConversationHasMore,
    setChannelConversationHasMoreBackward,
    setChannelConversationLoadingBackward,
    addChannelMessage,
    batchAddMissingMessages,
    toggleMessageSelection,
    resetSelectedMessages,
    updateMediaStatus,
    updateDeleteMessages,
    updateMessageReaction,
    // ✅ UI State Methods
    setChannelHasMore,
    setChannelLoading,
    resetChannelUiState,
    resetChannelUiStateOnSwitch,
    // ✅ New Channel Search Methods
    setChannelSearchText,
    setChannelSearchResults,
    setChannelSearchIndex,
    setChannelIsSearching,
    clearChannelSearch,
    removeUnreadSeparatorFromMessages,
    updateUnreadSeparatorLabel,
    decrementUnreadSeparatorLabel,
    clearChannelsConversationExceptActive,
    clearChannelConversation,
} = channelConversationSlice.actions;

export default channelConversationSlice.reducer;
